import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Button, Form, Input, Select, Space, Modal, Tag, message, Empty, Spin, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, HomeOutlined, BankOutlined, EnvironmentOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { addressService } from '../services/api';
import './CustomerDashboard.css';

const { Title, Text } = Typography;

const AddressManagement = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const res = await addressService.getAddresses();
      const data = res.data?.data || res.data || [];
      setAddresses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch addresses:', err);
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingAddress(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (address) => {
    setEditingAddress(address);
    form.setFieldsValue(address);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await addressService.deleteAddress(id);
      message.success('Address deleted');
      fetchAddresses();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await addressService.setDefaultAddress(id);
      message.success('Default address updated');
      fetchAddresses();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to update');
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (editingAddress) {
        await addressService.updateAddress(editingAddress.id, values);
        message.success('Address updated');
      } else {
        await addressService.addAddress(values);
        message.success('Address added');
      }
      setModalOpen(false);
      form.resetFields();
      fetchAddresses();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to save address');
    } finally {
      setSubmitting(false);
    }
  };

  const typeIcons = { home: <HomeOutlined />, office: <BankOutlined />, other: <EnvironmentOutlined /> };
  const typeColors = { home: 'green', office: 'blue', other: 'default' };

  if (loading) return <div className="customer-page" style={{ padding: 0, textAlign: 'center', paddingTop: 80 }}><Spin size="large" /></div>;

  return (
    <div className="customer-page" style={{ padding: 0 }}>
      <div className="customer-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={2}>My Addresses</Title>
          <Text type="secondary">Manage your delivery addresses</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal} className="customer-btn-primary">
          Add Address
        </Button>
      </div>

      {addresses.length === 0 ? (
        <Card><Empty description="No addresses saved yet. Add one to get started." /></Card>
      ) : (
        <Row gutter={[16, 16]}>
          {addresses.map((addr) => (
            <Col xs={24} sm={12} lg={8} key={addr.id}>
              <Card
                className="customer-card"
                style={{ border: addr.is_default ? '2px solid #52b788' : '1px solid #e8f0e8', position: 'relative' }}
                actions={[
                  <Tooltip title="Set as default">
                    <span onClick={() => handleSetDefault(addr.id)}>
                      {addr.is_default ? <StarFilled style={{ color: '#f1a134' }} /> : <StarOutlined />}
                    </span>
                  </Tooltip>,
                  <EditOutlined key="edit" onClick={() => openEditModal(addr)} />,
                  <DeleteOutlined key="delete" style={{ color: '#e5484d' }} onClick={() => {
                    Modal.confirm({ title: 'Delete address?', content: 'This cannot be undone.', onOk: () => handleDelete(addr.id) });
                  }} />,
                ]}
              >
                {addr.is_default && <Tag color="green" style={{ position: 'absolute', top: 8, right: 8 }}>Default</Tag>}
                <div style={{ marginBottom: 8 }}>
                  <Tag icon={typeIcons[addr.address_type] || <EnvironmentOutlined />} color={typeColors[addr.address_type] || 'default'}>
                    {(addr.address_type || 'other').toUpperCase()}
                  </Tag>
                </div>
                <Text strong style={{ display: 'block', color: '#2d5a3d' }}>{addr.full_name || addr.label}</Text>
                <Text style={{ display: 'block', color: '#6b7280', fontSize: 13 }}>
                  {addr.phone_number}
                </Text>
                <Text style={{ display: 'block', color: '#374151', marginTop: 4 }}>
                  {addr.address_line1}{addr.address_line2 ? `, ${addr.address_line2}` : ''}
                </Text>
                <Text style={{ display: 'block', color: '#6b7280', fontSize: 13 }}>
                  {[addr.city, addr.state, addr.country].filter(Boolean).join(', ') || 'Location not set'}
                </Text>
                {addr.landmark && <Text style={{ display: 'block', color: '#9ca3af', fontSize: 12, marginTop: 2 }}>📍 {addr.landmark}</Text>}
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal
        title={editingAddress ? 'Edit Address' : 'Add New Address'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        footer={null}
        width={500}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="full_name" label="Full Name" rules={[{ required: true }]}>
                <Input style={{ borderRadius: 10 }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone_number" label="Phone Number" rules={[{ required: true }, { pattern: /^\+?\d+$/, message: 'Numbers only' }]}>
                <Input style={{ borderRadius: 10 }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="address_line1" label="Address Line 1" rules={[{ required: true }]}>
            <Input style={{ borderRadius: 10 }} />
          </Form.Item>
          <Form.Item name="address_line2" label="Address Line 2">
            <Input style={{ borderRadius: 10 }} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="city" label="City" rules={[{ required: true }]}>
                <Input style={{ borderRadius: 10 }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="state" label="State">
                <Input style={{ borderRadius: 10 }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="country" label="Country">
                <Input style={{ borderRadius: 10 }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="address_type" label="Type">
                <Select style={{ borderRadius: 10 }}>
                  <Select.Option value="home">Home</Select.Option>
                  <Select.Option value="office">Office</Select.Option>
                  <Select.Option value="other">Other</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="landmark" label="Landmark">
                <Input style={{ borderRadius: 10 }} />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Space>
              <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={submitting} className="customer-btn-primary">
                {editingAddress ? 'Update Address' : 'Save Address'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AddressManagement;
